import {
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Spin,
} from "antd";
import {
  useTranslation,
} from "react-i18next";
import {
  BookOpen,
  Search,
} from "lucide-react";
import {
  type SalesOrder,
} from "./SalesProductAnalyticsTabsDashboard.data";
import {
  fetchShopifySalesProductSalesOrders,
  type ShopifySalesProductOrdersSort,
} from "src/services/ShopifySalesProductAPI";
import { normalizeSalesOrdersResponse } from "../utils/salesProductApiNormalize";
import { useDashboardPdfTableExpansion } from "src/hooks/useDashboardPdfTableExpansion";
import {
  getApiLangFromI18n,
} from "src/utils/apiLang";
import {
  getOptionalTenantId,
  useShopifySalesProductSalesCards,
  useShopifySalesProductOrders,
  useShopifySalesProductSalesByChannel,
} from "../hooks/useShopifySalesProductAnalytics";
import {
  DashboardKpiCard,
  DashboardNarrativePanel,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DashboardSectionHeader,
  DashboardExportButton,
  TablePagePagination,
  useDashboardTableSort,
  dashboardDataTable as dt,
} from "src/components/common";
import {
  useDashboardNarrative,
} from "src/hooks/useDashboardNarrative";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import {
  SalesByChannelChart,
} from "./SalesByChannelChart/SalesByChannelChart";
import {
  type SalesChannelMetric,
  PAGE_SIZE,
  formatCurrency,
  formatTableText,
  formatOrderDate,
  formatOrderTableCurrency,
  TOTAL_SALES_METRIC_LABEL,
  getMetricTooltip,
  useLocalizedMetricTooltip,
  SectionTitle,
  SelectMenu,
  TopChannelKpiCard,
  TotalOrdersByStatusCard,
  OrderDetailSidePanel,
} from "./salesProductShared";

export const SalesSection: FC<{
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ dateRange, onDateRangeChange }) => {
  const { t, i18n } = useTranslation();
  const lang = getApiLangFromI18n(i18n);
  const totalSalesTooltip = useLocalizedMetricTooltip("Revenue", getMetricTooltip("Revenue") ?? getMetricTooltip("Total Sales"));
  const tenantId = getOptionalTenantId();
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelMetric, setChannelMetric] = useState<SalesChannelMetric>("sales");
  const [snView] = useState<"default" | "reversed">("default");
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const { cards: salesCards, rawData: salesCardsRawData, loading: cardsLoading, error: cardsError } =
    useShopifySalesProductSalesCards(
      dateRange.startDate,
      dateRange.endDate,
      lang,
      tenantId,
    );
  const { channels: channelData, rawData: salesByChannelRawData, loading: channelsLoading, error: channelsError } =
    useShopifySalesProductSalesByChannel(
      dateRange.startDate,
      dateRange.endDate,
      lang,
      tenantId,
      statusFilter,
      channelMetric,
    );

  type SalesOrdersListSortKey = "date" | "total";
  const salesOrdersColumnSort = useDashboardTableSort<SalesOrdersListSortKey>({
    key: "date",
    direction: "desc",
  });
  const ordersFetchSort = useMemo<ShopifySalesProductOrdersSort>(() => {
    if (salesOrdersColumnSort.sort.key === "date") {
      return salesOrdersColumnSort.sort.direction === "desc" ? "date_desc" : "date_asc";
    }
    return salesOrdersColumnSort.sort.direction === "desc" ? "total_desc" : "total_asc";
  }, [salesOrdersColumnSort.sort]);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchTimerRef.current) { clearTimeout(searchTimerRef.current); }
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => {
      if (searchTimerRef.current) { clearTimeout(searchTimerRef.current); }
    };
  }, [search]);

  const {
    orders,
    total: ordersTotal,
    rawData: ordersRawData,
    loading: ordersLoading,
    error: ordersError,
  } = useShopifySalesProductOrders(
    dateRange.startDate,
    dateRange.endDate,
    lang,
    tenantId,
    page,
    PAGE_SIZE,
    ordersFetchSort,
    debouncedSearch,
    statusFilter,
  );

  const { rows: exportAwareOrders, onBeforeCapture: onBeforeExportCapture, onAfterCapture: onAfterExportCapture } =
    useDashboardPdfTableExpansion(orders, async (page, pageSize) => {
      const raw = await fetchShopifySalesProductSalesOrders(dateRange.startDate, dateRange.endDate, {
        tenantId,
        lang,
        page,
        pageSize,
        sort: ordersFetchSort,
        search: debouncedSearch || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      const normalized = normalizeSalesOrdersResponse(raw);
      return { rows: normalized.orders, total: normalized.total };
    });

  const displayedOrders = useMemo(() => {
    if (snView !== "reversed") {
      return exportAwareOrders;
    }
    return [...exportAwareOrders].reverse();
  }, [exportAwareOrders, snView]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, dateRange.startDate, dateRange.endDate, ordersFetchSort]);

  const totalPages = Math.max(1, Math.ceil(ordersTotal / PAGE_SIZE));
  const channelMetricIsOrders = channelMetric === "orders";
  const channelTitle = channelMetricIsOrders
    ? t("salesProduct.sales.channel.titleOrders")
    : t("salesProduct.sales.channel.titleSales");
  const channelSubtitle = channelMetricIsOrders
    ? t("salesProduct.sales.channel.subtitleOrders")
    : t("salesProduct.sales.channel.subtitleSales");
  const chartValueLabel = channelMetricIsOrders
    ? t("salesProduct.sales.channel.chartLabelOrders")
    : t("salesProduct.sales.kpi.totalSales", { defaultValue: TOTAL_SALES_METRIC_LABEL });
  const narrativePayloadData = useMemo(
    () => ({
      salesCards: salesCardsRawData,
      salesByChannel: salesByChannelRawData,
      orders: ordersRawData,
    }),
    [ordersRawData, salesByChannelRawData, salesCardsRawData],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "sales_product_analytics",
    tabKey: "sales",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    data: narrativePayloadData,
    enabled:
      !cardsLoading &&
      !channelsLoading &&
      !ordersLoading &&
      !cardsError &&
      !channelsError &&
      !ordersError &&
      Boolean(salesCardsRawData) &&
      Boolean(salesByChannelRawData) &&
      Boolean(ordersRawData),
  });
  const openOrderDetail = (order: SalesOrder) => {
    setSelectedOrderId(order.id);
  };

  return (
    <div className="sales-product-analytics-dashboard__section" ref={pdfContainerRef}>
      <DashboardSectionHeader
        title={t("salesProduct.sales.header.title")}
        subtitle={t("salesProduct.sales.header.subtitle")}
        sticky
        actions={(
          <>
            <OrganicDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onRangeChange={onDateRangeChange}
            />
            <DashboardExportButton
              dashboardName="Sales"
              dateRange={dateRange}
              containerRef={pdfContainerRef}
              disabled={cardsLoading || channelsLoading || ordersLoading}
              onBeforeCapture={onBeforeExportCapture}
              onAfterCapture={onAfterExportCapture}
            />
          </>
        )}
      />

      {channelsError ? (
        <Alert type="warning" showIcon message={channelsError} style={{ marginBottom: 12 }} />
      ) : null}
      {cardsError ? (
        <Alert type="warning" showIcon message={cardsError} style={{ marginBottom: 12 }} />
      ) : null}
      {ordersError ? (
        <Alert type="error" showIcon message={ordersError} style={{ marginBottom: 12 }} />
      ) : null}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={narrative}
          loading={cardsLoading || channelsLoading || ordersLoading || narrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      <Spin spinning={cardsLoading}>
        <div
          className="sales-product-analytics-dashboard__metrics sales-product-analytics-dashboard__metrics--sales"
          data-pdf-block="kpi-cards"
        >
          <DashboardKpiCard
            title={t("salesProduct.sales.kpi.totalSales", { defaultValue: TOTAL_SALES_METRIC_LABEL })}
            value={salesCards?.totalRevenueFormattedValue ?? formatCurrency(salesCards?.totalRevenue ?? 0, "PKR")}
            icon={<BookOpen size={22} />}
            tooltip={totalSalesTooltip}
            formulaTitle={t("salesProduct.shared.formula")}
            hideNoTrendFallback
          />
          <TopChannelKpiCard channelLabel={salesCards?.topChannel?.label ?? t("salesProduct.sales.kpi.notAvailable")} />
          <TotalOrdersByStatusCard
            totalOrders={salesCards?.totalOrders ?? ordersTotal}
            totalOrdersFormattedValue={salesCards?.totalOrdersFormattedValue}
            breakdown={salesCards?.orderStatusBreakdown ?? []}
          />
        </div>
      </Spin>

      <Spin spinning={channelsLoading}>
        <section
          className="sales-product-analytics-dashboard__card sales-product-analytics-dashboard__card--sales-by-channel"
          data-pdf-block="sales-by-channel"
        >
          <div className="sales-product-analytics-dashboard__sales-channel-head">
            <div>
              <SectionTitle
                title={channelTitle}
                tooltipKey={channelMetricIsOrders ? "Orders by Channel" : "Sales by Channel"}
              />
              <p className="sales-product-analytics-dashboard__card-subtitle">
                {channelSubtitle}
              </p>
            </div>
            <label className="sales-product-analytics-dashboard__channel-toggle">
              <span>
                {channelMetricIsOrders
                  ? t("salesProduct.sales.channel.toggleShowSales")
                  : t("salesProduct.sales.channel.toggleShowOrders")}
              </span>
              <input
                type="checkbox"
                checked={channelMetricIsOrders}
                onChange={event => setChannelMetric(event.target.checked ? "orders" : "sales")}
              />
              <i aria-hidden />
            </label>
          </div>
          <SalesByChannelChart
            channels={channelData}
            currencyLabel="PKR"
            valueKind={channelMetricIsOrders ? "number" : "currency"}
            showSummary={false}
            emptyMessage={
              channelMetricIsOrders
                ? t("salesProduct.sales.channel.emptyOrders")
                : t("salesProduct.sales.channel.emptySales")
            }
            summaryLabels={{
              totalLabel: chartValueLabel,
              tooltipMetricLabel: chartValueLabel,
              tooltipShareQualifier: channelMetricIsOrders
                ? t("salesProduct.sales.channel.qualifierOrders")
                : t("salesProduct.sales.channel.qualifierSales"),
              ariaLabel: `${chartValueLabel} by channel, horizontal bars sorted by value`,
              summaryAriaLabel: `${chartValueLabel} by channel summary`,
              topHint: "",
              totalHint: "",
            }}
          />
        </section>
      </Spin>

      <Spin spinning={ordersLoading}>
        <DashboardDataTablePanel
          as="section"
          className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--orders"
          data-pdf-block="orders-table"
          data-pdf-splittable
        >
          <DashboardDataTablePanelHeader>
            <div className="sales-product-analytics-dashboard__orders-heading">
              <SectionTitle title={t("salesProduct.sales.orders.title")} tooltipKey="Orders" />
              <p className="sales-product-analytics-dashboard__orders-count">
                {t("salesProduct.sales.orders.countLabel", { value: ordersTotal.toLocaleString() })}
              </p>
            </div>
            <div className="sales-product-analytics-dashboard__panel-tools">
              <label className={`${dt.search} ${dt.searchSmall}`.trim()}>
                <Search size={16} />
                <input
                  type="search"
                  placeholder={t("salesProduct.sales.orders.searchPlaceholder")}
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                />
              </label>
              <SelectMenu
                className="sales-product-analytics-dashboard__select-menu--toolbar sales-product-analytics-dashboard__select-menu--orders"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: "all", label: t("salesProduct.sales.orders.statusAll") },
                  { value: "fulfilled", label: t("salesProduct.sales.orders.statusFulfilled") },
                  { value: "unfulfilled", label: t("salesProduct.sales.orders.statusUnfulfilled") },
                ]}
              />

            </div>
          </DashboardDataTablePanelHeader>
          <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
            <DashboardDataTableTable className="sales-product-analytics-dashboard__table">
              <thead>
                <tr>

                  <th>{t("salesProduct.sales.orders.columnOrderNumber")}</th>
                  <th>{t("salesProduct.sales.orders.columnCustomerName")}</th>
                  <th className={dt.number}>
                    <DashboardDataTableSortButton
                      onClick={() => salesOrdersColumnSort.toggleColumn("total")}
                    >
                      {t("salesProduct.sales.orders.columnOrderTotal")}
                    </DashboardDataTableSortButton>
                  </th>
                  <th>{t("salesProduct.sales.orders.columnOrderStatus")}</th>
                  <th>
                    <DashboardDataTableSortButton
                      onClick={() => salesOrdersColumnSort.toggleColumn("date")}
                    >
                      {t("salesProduct.sales.orders.columnDate")}
                    </DashboardDataTableSortButton>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedOrders.map(order => {
                  return (
                    <tr
                      key={order.id}
                      className="sales-product-analytics-dashboard__order-row"
                      onClick={() => openOrderDetail(order)}
                      onKeyDown={event => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openOrderDetail(order);
                        }
                      }}
                      tabIndex={0}
                      aria-label={`View details for order ${order.orderName}`}
                    >

                      <td>{formatTableText(order.orderName)}</td>
                      <td>{formatTableText(order.customerFullName)}</td>
                      <td className={dt.number}>{order.totalPriceFormattedValue ?? formatOrderTableCurrency(order.totalPrice)}</td>
                      <td><span className={`sales-product-analytics-dashboard__status sales-product-analytics-dashboard__status--${order.orderStatus || "unknown"}`}>{order.orderStatusLabel ?? order.orderStatus ?? "—"}</span></td>
                      <td>{order.dateFormattedValue ?? formatOrderDate(order.date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
          <TablePagePagination
            currentPage={page}
            totalPages={totalPages}
            onPrevious={() => setPage(p => Math.max(1, p - 1))}
            onNext={() =>
              setPage(p => Math.min(totalPages, p + 1))
            }
          />
        </DashboardDataTablePanel>
      </Spin>
      <OrderDetailSidePanel
        open={Boolean(selectedOrderId)}
        orderId={selectedOrderId}
        lang={lang}
        tenantId={tenantId}
        onClose={() => setSelectedOrderId(null)}
      />
    </div>
  );
};
